import {
  ColDef,
  KeyCreatorParams,
  ValueFormatterParams,
} from 'ag-grid-community';
import { DateTime } from 'luxon';
import { AG_GRID_LOCALE_ES } from '@ag-grid-community/locale';

export const DEFAULT_LOCALIZATION = AG_GRID_LOCALE_ES;

export const PAGE_SIZE_SELECTOR = [10, 20, 50, 100, 500, 1000];

export const DEFAULT_COL_DEF: ColDef = {
  menuTabs: [],
  floatingFilter: true,
  editable: false,
};

export const DEFAULT_CLIENT_COL_DEF: ColDef = {
  menuTabs: [],
  floatingFilter: false,
  editable: false,
};

export const TEXT_FILTER_PARAMS = {
  filterOptions: ['contains'],
  maxNumConditions: 1,
  buttons: ['clear'],
  browserAutoComplete: false,
};

export const NUMERIC_FILTER_PARAMS = {
  allowedCharPattern: /\d/,
  maxNumConditions: 1,
  filterOptions: ['equals', 'lessThanOrEqual', 'greaterThanOrEqual', 'inRange'],
  buttons: ['clear'],
  browserAutoComplete: false,
};

export const BOOLEAN_FILTER_PARAMS = {
  suppressSorting: true,
  debounceMs: 500,
  filterOptions: ['equals'],
  maxNumConditions: 1,
  buttons: ['clear'],
  values: [
    { id: true, label: 'Sí' },
    { id: false, label: 'No' },
  ],
  keyCreator({ value }: KeyCreatorParams) {
    return value.id;
  },
  valueFormatter({ value }: ValueFormatterParams) {
    return value.label;
  },
};

export const dateFilterParams = () => ({
  maxNumConditions: 1,
  maxValidDate: new Date(),
  filterOptions: ['equals', 'lessThanOrEqual', 'greaterThanOrEqual', 'inRange'],
  buttons: ['clear'],
});

export const enumFilterParams = <T extends string | number>(
  options: T[],
  localeMap: Record<T, string>
) => {
  const values = options.map((option) => ({
    id: option,
    label: localeMap[option],
  }));

  return {
    suppressSorting: true,
    debounceMs: 500,
    values,
    keyCreator({ value }: KeyCreatorParams) {
      return value?.id ?? value ?? null;
    },
    valueFormatter({ value }: ValueFormatterParams) {
      return value?.label ?? value ?? '';
    },
  };
};

export const dateSortComparator =
  <T>(fieldName: keyof T): ColDef<T>['comparator'] =>
  (_, __, a, b, isDescending) => {
    if (!a.data || !b.data) {
      return 0;
    }

    if (!a.data[fieldName]) {
      return isDescending ? -1 : 1;
    }

    if (!b.data[fieldName]) {
      return isDescending ? 1 : -1;
    }

    const dateA = a.data[fieldName] as string;
    const dateB = b.data[fieldName] as string;

    return (
      DateTime.fromISO(dateA).toMillis() - DateTime.fromISO(dateB).toMillis()
    );
  };

export const numericSortComparator =
  <T>(fieldName: keyof T): ColDef<T>['comparator'] =>
  (_, __, a, b, isDescending) => {
    if (!a.data || !b.data) {
      return 0;
    }

    if (!a.data[fieldName]) {
      return isDescending ? -1 : 1;
    }

    if (!b.data[fieldName]) {
      return isDescending ? 1 : -1;
    }

    const numA = a.data[fieldName] as number;
    const numB = b.data[fieldName] as number;

    return numA - numB;
  };
